import { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ConfirmDialog from "../components/ConfirmDialog";

import customerApi from "../api/customerApi";
import orderApi from "../api/orderApi";
import priceListApi from "../api/priceListApi";

const businessTypes = ["Công ty", "Hộ kinh doanh", "Cá nhân kinh doanh"];
const calculationMethod = [
  { value: 1, label: "Trực tiếp" },
  { value: 2, label: "Khấu trừ" },
];
function isValidEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}
function isGuid(str) {
  const guidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return guidRegex.test(str);
}
const pageNo = "CustomerInforPage";
export default function CustomerInforPage() {
  const user = useSelector((state) => state.auth.user);
  const customerInfor = useSelector((state) => state.auth.customerInfor);
  const roles = useSelector((state) => state.auth.roles);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [productGroups, setProductGroups] = useState([]);
  const [productGroup, setProductGroup] = useState("");
  const [priceList, setPriceList] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isExtra, setIsExtra] = useState(false);
  const steps = ["Đơn hàng", "Thanh toán"];
  const [openAddEditInvoiceModal, setOpenAddEditInvoiceModal] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [customerParent, setCustomerParent] = useState(null);
  const [customerNoParent, setCustomerNoParent] = useState("");
  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: () => {},
    onClose: () => {},
  });

  const getFilteredMethods = () => {
    if (customer?.CusBusinessType === "Công ty") {
      return calculationMethod.filter((method) => method.value === 2);
    } else {
      return calculationMethod.filter((method) => method.value === 1);
    }
  };

  const filteredMethods = getFilteredMethods();

  const fetchCustomerInfor = async () => {
    try {
      const response = await customerApi.getCustomerInfor(customerInfor?.CusId);
      if (response.success) {
        setCustomer(response.data);
      } else {
        toast.error("Lỗi kết nối server");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCustomerInfor();
  }, []);

  const fetchProductGroup = useCallback(async () => {
    if (customer === null) return;
    try {
      const response = await priceListApi.getProductGroup(
        1,
        customer?.SupplierId,
      );
      if (response.success) {
        setProductGroups(response.data);
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, [customer]);
  useEffect(() => {
    fetchProductGroup();
  }, [fetchProductGroup]);

  const handleOpenInvoiceModal = async () => {
    if (customer?.SupplierName === "" || customer?.SupplierName === null) {
      toast.warning("Vui lòng cấu hình nhà cung cấp hóa đơn.");
      return;
    }
    try {
      let customerData = null;
      const reCustomer = await customerApi.getCustomerInfor(
        customerInfor?.CusId,
      );
      if (reCustomer.success) {
        customerData = reCustomer.data;
      }
      const response = await orderApi.getLastOrder(customerInfor?.CusId, 1);
      if (response.success) {
        if (response.data === null) {
          setIsExtra(true);
        } else {
          setIsExtra(false);
        }
      }
      setCurrentOrder({
        orderId: null,
        orderCode: null,
        productName: "",
        unitName: "",
        unitPrice: 0,
        quantity: 0,
        itemAmount: 0,
        discountPercent: 0,
        discountAmount: 0,
        vatPercent: 0,
        vatAmount: 0,
        extraAmount: 0,
        totalAmount: 0,
        orderType: 1,
        startTime: dayjs(customerData.CusExpiredDate),
        endTime: null,
        note: "",
      });
      await fetchProductGroup();
      setOpenAddEditInvoiceModal(true);
    } catch (error) {
      toast.error(error.message);
    }
  };
  const fetchPriceList = useCallback(
    async (
      searchKeyword,
      page,
      pageSize,
      productType,
      supplierId,
      productGroup,
    ) => {
      setLoading(true);
      try {
        const response = await priceListApi.search(
          searchKeyword,
          page,
          pageSize,
          productType,
          supplierId,
          productGroup,
        );
        if (response.success) {
          setPriceList(response.data);
        } else {
          setPriceList([]);
        }
      } catch (error) {
        toast.error(error.toString() || "Đã xảy ra lỗi khi tìm kiếm mặt hàng.");
        setPriceList([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  const handleCloseAddEditInvoiceModal = () => {
    setOpenAddEditInvoiceModal(false);
    setCurrentOrder(null);
    setIsExtra(false);
    setActiveStep(0);
  };
  const handleCreateOrder = async () => {
    if (currentOrder?.priceListId == null) {
      toast.error("Vui lòng chọn gói cước");
      return;
    }
    try {
      const response = await orderApi.createOrder(
        currentOrder.orderId,
        currentOrder.orderType,
        currentOrder.priceListId,
        isExtra,
        currentOrder.note,
        currentOrder.startTime,
        customerInfor?.CusId,
        user.UsId,
      );
      if (response.success) {
        setCurrentOrder(response.data);
        setActiveStep(1);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  const handleNext = async () => {
    if (activeStep === 0) {
      await handleCreateOrder();
    } else if (activeStep === steps.length - 1) {
      let status = await handleCheckpayTransaction();
      if (status) {
        handleCloseAddEditInvoiceModal();
      }
    }
  };
  const handleCheckpayTransaction = async () => {
    let success = false;
    try {
      const response = await orderApi.checkPaymentStatus(currentOrder?.orderId);
      if (response.success) {
        success = true;
        toast.success("Đã nhận được thanh toán");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
    return success;
  };

  const handleEditCustomer = async (customerId) => {
    if (
      !roles.some(
        (x) =>
          x.PvNo === pageNo &&
          x.CtrNo === "btnUpdateInfor" &&
          x.IsApproved === true,
      )
    ) {
      toast.warning("Bạn không có quyền cập nhật thông tin.");
      return;
    }
    try {
      const response = await customerApi.getCustomerInfor(customerId);
      if (response.success) {
        setCustomer(response.data);
        setFormErrors({});
        setOpenAddEditModal(true);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCloseAddEditModal = () => {
    setOpenAddEditModal(false);
    setFormErrors({});
  };

  const handleSaveCustomer = async () => {
    setLoading(true);

    const errors = {};
    // Validate các trường bắt buộc
    if (!customer.CusTaxId) {
      errors.cusTaxId = "Mã số thuế không được để trống.";
    }
    if (!customer.CusCompanyName) {
      errors.cusCompanyName = "Tên đơn vị không được để trống.";
    }
    if (!customer.CusBusinessType) {
      errors.cusBusinessType = "Loại hình doanh nghiệp không được để trống.";
    }
    if (!customer.CusEmail) {
      errors.cusEmail = "Email không được để trống.";
    } else if (!isValidEmail(customer.CusEmail))
      errors.cusEmail = "Email không hợp lệ";
    if (!customer.CusRepresentativeName) {
      errors.cusRepresentativeName = "Người đại diện không được để trống.";
    }
    if (
      customer.CusEmailReceiveInvoice &&
      !isValidEmail(customer.CusEmailReceiveInvoice)
    )
      errors.cusEmailReceiveInvoice = "Email không hợp lệ";

    // if (
    //   !filteredMethods.find(
    //     (method) => method.value === customer.CusTaxCalculationMethod,
    //   )
    // ) {
    //   customer.CusTaxCalculationMethod = null;
    // }
    // if (customer.CusTaxCalculationMethod == null)
    //   errors.cusTaxCalculationMethod =
    //     "Phương pháp tính thuế không được để trống.";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Vui lòng điền đầy đủ các trường bắt buộc.", "error");
      setLoading(false);
      return;
    }
    try {
      const response = await customerApi.upsertCustomer(customer);
      if (response.success) {
        toast.success("Lưu khách hàng thành công!");
        handleCloseAddEditModal();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Lỗi khi lưu khách hàng:", error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.error("Lỗi khi sao chép: ", err);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }
  const handleChangeCustomerNoParent = async (value) => {
    setCustomerNoParent(value);
    try {
      if (isGuid(value)) {
        const response = await customerApi.getCustomerInforByGuid(value);
        if (response.success) {
          setCustomerParent(response.data);
        } else {
          setCustomerParent(null);
        }
      } else setCustomerParent(null);
    } catch (error) {
      setCustomerParent(null);
    }
  };
  const handleLinkParentCustomer = async (step, isLink) => {
    let button = isLink ? "btnLinkMerchant" : "btnUnLinkMerchant";
    if (
      !roles.some(
        (x) => x.PvNo === pageNo && x.CtrNo === button && x.IsApproved === true,
      )
    ) {
      toast.warning(
        "Bạn không có quyền " +
          (isLink ? "liên kết" : "gỡ liên kết") +
          " Merchant.",
      );
      return;
    }
    if (step === 0) {
      if (isLink) {
        if (customerNoParent === "" || customerNoParent === null) {
          toast.error("Vui lòng nhập mã Merchant cha");
          return;
        }
        if (customerParent === null) {
          toast.error("Merchant cha không tồn tại");
          return;
        }
        if (customer?.CusId === customerParent?.CusId) {
          toast.error("Không thể liên kết với chính mình");
          return;
        }

        setConfirmDialogState({
          open: true,
          title: "Liên kết Merchant cha",
          content:
            "Sau khi liên kết số lượng hóa đơn được sử dụng và hạn sử dụng phần mềm sẽ phụ thuộc vào Merchant cha. Bạn chắc chắn muốn tiếp tục liên kết?",
          onConfirm: () => {
            handleLinkParentCustomer(1, isLink);
            setConfirmDialogState({
              ...confirmDialogState,
              open: false,
            });
          },
          onClose: () =>
            setConfirmDialogState({
              ...confirmDialogState,
              open: false,
            }),
        });
      } else {
        setConfirmDialogState({
          open: true,
          title: "Gỡ liên kết",
          content: "Bạn chắc chắn muốn gỡ liên kết?",
          onConfirm: () => {
            handleLinkParentCustomer(1, isLink);
            setConfirmDialogState({
              ...confirmDialogState,
              open: false,
            });
          },
          onClose: () =>
            setConfirmDialogState({
              ...confirmDialogState,
              open: false,
            }),
        });
      }
    } else {
      try {
        const response = await customerApi.linkParentCustomer(
          isLink,
          customer?.CusId,
          customerParent?.CusId,
        );
        if (response.success) {
          toast.success("Thành công");
          handleCloseLinkModal();
          fetchCustomerInfor();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const handleCloseLinkModal = () => {
    setOpenLinkModal(false);
    setCustomerParent(null);
    setCustomerNoParent("");
  };

  const renderRow = (label, value, isLink = false, minWidth = "180px") => (
    <Box display="flex" mb={1}>
      <Typography
        sx={{
          minWidth: minWidth,
          color: "text.primary",
          ml: 1,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ color: "text.secondary" }}>
        {isLink && value ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", color: "#1976d2" }}
          >
            {value}
          </a>
        ) : value === "" || value === null ? (
          "—"
        ) : (
          value
        )}
      </Typography>
    </Box>
  );
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
      <Box>
        <Grid container justifyContent="flex-start" sx={{ margin: 3 }}>
          <Grid
            component="fieldset"
            sx={{ borderRadius: 3, mb: 1 }}
            size={{ xs: 12 }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                mb: 1,
                fontSize: 18,
              }}
            >
              Thông tin phần mềm và dịch vụ
            </Typography>
            <Grid size={{ xs: 12 }} display="flex">
              <Grid sx={{ width: "400px", mr: 1 }}>
                <Box display="flex" mb={1}>
                  <Typography
                    sx={{
                      minWidth: "200px",
                      color: "text.primary",
                      ml: 1,
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    Số hoá đơn còn lại:
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {customer?.CusInvoiceQuantityRemain}
                  </Typography>
                </Box>
                <Box display="flex" mb={1}>
                  <Typography
                    sx={{
                      minWidth: "200px",
                      color: "text.primary",
                      ml: 1,
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    Gói phần mềm quản lý hóa đơn MTT
                  </Typography>
                </Box>
                {renderRow(
                  "Số cửa hàng:",
                  customer?.InvoiceSignActive || 0,
                  false,
                  "200px",
                )}
              </Grid>
              <Grid sx={{ width: "400px", mr: 1 }}>
                {renderRow("Nhà cung cấp hóa đơn: ", customer?.SupplierName)}
                {renderRow(
                  "Ngày thanh toán tiếp theo:",
                  customer?.CusExpiredDate != null
                    ? dayjs(customer?.CusExpiredDate).format("DD/MM/YYYY")
                    : "—",
                )}
              </Grid>
              <Grid sx={{ width: "400px" }}>
                <Box display="flex" mb={1}>
                  <Typography>
                    <i
                      style={{
                        textDecoration: "underline",
                        color: "#1976d2",
                        cursor: "pointer",
                      }}
                      onClick={handleOpenInvoiceModal}
                    >
                      Mua thêm hóa đơn
                    </i>
                  </Typography>
                </Box>
                <Box display="flex" mb={1}>
                  <Typography>
                    <i
                      style={{
                        textDecoration: "underline",
                        color: "#1976d2",
                        cursor: "pointer",
                      }}
                    >
                      Xem các gói phần mềm khác
                    </i>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid component="fieldset" sx={{ borderRadius: 3 }} size={{ xs: 12 }}>
            <Typography
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                mb: 1,
                fontSize: 18,
              }}
            >
              Thông tin đơn vị
            </Typography>

            <Grid
              sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}
            >
              <Grid sx={{ width: "800px" }}>
                <Box display="flex" mb={1}>
                  <Typography
                    sx={{
                      minWidth: "180px",
                      color: "text.primary",
                      ml: 1,
                    }}
                  >
                    Merchant cha:
                  </Typography>
                  <Typography sx={{ color: "text.secondary", mr: 1 }}>
                    {customer?.customerCompanyParentName
                      ? customer?.customerCompanyParentName
                      : "Không có"}
                  </Typography>
                  {customer?.customerCompanyParentName ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleLinkParentCustomer(0, false)}
                    >
                      Gỡ liên kết
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => {
                        if (customerInfor?.hasChild) {
                          toast.warning(
                            "Merchant này không thể thực hiện liên kết do đang cha của một Merchant khác.",
                          );
                          return;
                        }
                        setOpenLinkModal(true);
                      }}
                    >
                      Liên kết
                    </Button>
                  )}
                </Box>
                {renderRow("Mã số thuế:", customer?.CusTaxId)}
                {renderRow("Tổ chức/Cá nhân:", customer?.CusCompanyName)}
                {renderRow("Địa chỉ:", customer?.CusAddress)}
                {renderRow("Người đại diện:", customer?.CusRepresentativeName)}
                {renderRow("Số điện thoại:", customer?.CusPhoneNumber)}
                {renderRow("Email liên hệ:", customer?.CusEmail)}
                {renderRow(
                  "Email nhận hóa đơn:",
                  customer?.CusEmailReceiveInvoice,
                )}
                {renderRow("Website:", customer?.CusWebsite, true)}
                <Box display="flex">
                  <Typography
                    sx={{
                      minWidth: "180px",
                      color: "text.primary",
                      ml: 1,
                    }}
                  >
                    Mã Merchant:
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {customer?.CusNo}
                  </Typography>
                  <Tooltip title="Sao chép">
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => handleCopy(customer?.CusNo)}
                    >
                      Sao chép
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid
                sx={{
                  width: "400px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Grid>
                  {renderRow(
                    "Loại hình doanh nghiệp:",
                    customer?.CusBusinessType,
                  )}
                  {renderRow(
                    "Ngày thành lập:",
                    customer?.CusCompanyEstablishmentDate
                      ? dayjs(customer?.CusCompanyEstablishmentDate).format(
                          "DD/MM/YYYY",
                        )
                      : "—",
                  )}
                  {renderRow(
                    "Ngày khởi tạo:",
                    customer?.CusCreatedDate
                      ? dayjs(customer?.CusCreatedDate).format("DD/MM/YYYY")
                      : "—",
                  )}
                </Grid>
                <Grid>
                  <Box display="flex">
                    <Typography>
                      <i
                        style={{
                          textDecoration: "underline",
                          color: "#1976d2",
                          cursor: "pointer",
                          alignContent: "end",
                        }}
                        onClick={() => handleEditCustomer(customer?.CusId)}
                      >
                        Cập nhật thông tin
                      </i>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Dialog open={openAddEditInvoiceModal} maxWidth="sm" fullWidth>
          <DialogTitle>Đăng ký mua hoá đơn</DialogTitle>
          <Divider />
          <DialogContent>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label, index) => (
                <Step key={index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {activeStep === 0 && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="h6">
                  Tổ chức truyền nhận và lưu trữ: {customer?.SupplierName}
                </Typography>
                <TextField
                  select
                  label="Loại hoá đơn"
                  value={currentOrder?.productGroupNo || productGroup}
                  onChange={(e) => {
                    setProductGroup(e.target.value);
                    fetchPriceList(
                      "",
                      0,
                      100,
                      1,
                      customer?.SupplierId,
                      e.target.value,
                    );
                  }}
                  size="small"
                  sx={{ width: 300 }}
                >
                  {productGroups?.map((option) => (
                    <MenuItem
                      key={option.productGroupNo}
                      value={option.productGroupNo}
                    >
                      {option.productGroupName}
                    </MenuItem>
                  ))}
                </TextField>
                <Grid sx={{ display: "flex" }}>
                  <TextField
                    select
                    label="Gói cước"
                    value={currentOrder?.priceListId}
                    onChange={(e) => {
                      let price = priceList.find(
                        (option) => option.priceListId === e.target.value,
                      );
                      let itemAmount = price?.unitPrice * price?.quantity;
                      let vatAmount = (itemAmount * price?.vatPercent) / 100;
                      let extra = 0;
                      if (isExtra) extra = price.extraPrice;
                      setCurrentOrder((prev) => ({
                        ...prev,
                        priceListId: e.target.value,
                        quantity: price?.quantity,
                        unitPrice: price?.unitPrice,
                        unitName: price?.unitName,
                        productName: price?.productName,
                        itemAmount: itemAmount,
                        vatAmount: vatAmount,
                        extraAmount: extra,
                        totalAmount: itemAmount + vatAmount + extra,
                      }));
                    }}
                    size="small"
                    sx={{ width: 300 }}
                  >
                    {priceList.map((option) => (
                      <MenuItem
                        key={option.priceListId}
                        value={option.priceListId}
                      >
                        {option.productName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography sx={{ ml: 2, alignContent: "end" }}>
                    <strong>Số lượng:</strong> {currentOrder?.quantity}{" "}
                    {currentOrder?.unitName}
                  </Typography>
                </Grid>
                <Box display="flex" alignItems="center" mb={0}>
                  <Typography
                    variant="h7"
                    sx={{ minWidth: 150, textAlign: "start", mr: 1 }}
                  >
                    <strong>Phí khởi tạo lần đầu:</strong>
                  </Typography>
                  <Typography variant="h7" fullWidth>
                    {currentOrder?.extraAmount.toLocaleString("vi-VN")} VNĐ
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb={0}>
                  <Typography
                    variant="h7"
                    sx={{ minWidth: 150, textAlign: "start", mr: 1 }}
                  >
                    <strong>Tổng tiền:</strong>
                  </Typography>
                  <Typography variant="h7" fullWidth>
                    {currentOrder?.totalAmount.toLocaleString("vi-VN")} VNĐ{" "}
                    {"(Đã bao gồm VAT và phí khởi tạo)"}
                  </Typography>
                </Box>
              </Box>
            )}
            {activeStep === 1 && (
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
                height={"500px"}
              >
                {/* <Grid sx={{ backgroundColor: "red", height: 100 }}></Grid> */}
                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flex: 1,
                  }}
                >
                  <Box
                    component="iframe"
                    src={currentOrder?.qrLink}
                    title="QR Thanh toán"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      height: "100%",
                      zoom: 0.75,
                      transform: "scale(0.95)",
                      width: "70%",
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditInvoiceModal} color="primary">
              Hủy
            </Button>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack}>
                Trờ về
              </Button>
            )}
            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                "Thanh toán"
              ) : (
                "Tiếp tục"
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openAddEditModal}
          onClose={handleCloseAddEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Sửa thông tin</DialogTitle>
          <DialogContent>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid size={4}>
                <TextField
                  label="Mã số thuế"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusTaxId}
                  error={!!formErrors.cusTaxId}
                  helperText={formErrors.cusTaxId}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusTaxId: e.target.value,
                    }));
                    setFormErrors((prev) => ({ ...prev, cusTaxId: "" }));
                  }}
                  size="small"
                  required
                />
              </Grid>
              <Grid size={8}>
                <TextField
                  autoFocus
                  label="Tên đơn vị"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusCompanyName}
                  error={!!formErrors.cusCompanyName}
                  helperText={formErrors.cusCompanyName}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusCompanyName: e.target.value,
                    }));
                    setFormErrors((prev) => ({ ...prev, cusCompanyName: "" }));
                  }}
                  size="small"
                  required
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Địa chỉ đơn vị"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusAddress}
                  error={!!formErrors.cusAddress}
                  helperText={formErrors.cusAddress}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusAddress: e.target.value,
                    }));
                    setFormErrors((prev) => ({ ...prev, cusAddress: "" }));
                  }}
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  select
                  label="Loại hình doanh nghiệp"
                  fullWidth
                  value={customer?.CusBusinessType}
                  onChange={(e) =>
                    setCustomer((prev) => ({
                      ...prev,
                      CusBusinessType: e.target.value,
                    }))
                  }
                  size="small"
                >
                  {businessTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label="Ngày thành lập"
                  format="DD/MM/YYYY"
                  value={
                    customer?.CusCompanyEstablishmentDate
                      ? dayjs(customer.CusCompanyEstablishmentDate)
                      : dayjs()
                  }
                  onChange={(newValue) =>
                    setCustomer((prev) => ({
                      ...prev,
                      CusCompanyEstablishmentDate: newValue,
                    }))
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  id="representative"
                  label="Người đại diện"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusRepresentativeName}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusRepresentativeName: e.target.value,
                    }));
                    setFormErrors((prev) => ({
                      ...prev,
                      cusRepresentativeName: "",
                    }));
                  }}
                  error={!!formErrors.cusRepresentativeName}
                  helperText={formErrors.cusRepresentativeName}
                  size="small"
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Chức vụ"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusPosition}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusPosition: e.target.value,
                    }));
                    setFormErrors((prev) => ({
                      ...prev,
                      cusPosition: "",
                    }));
                  }}
                  error={!!formErrors.cusPosition}
                  helperText={formErrors.cusPosition}
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Email liên hệ"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusEmail}
                  error={!!formErrors.cusEmail}
                  helperText={formErrors.cusEmail}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusEmail: e.target.value,
                    }));
                    setFormErrors((prev) => ({ ...prev, cusEmail: "" }));
                  }}
                  size="small"
                  required
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Email nhận hóa đơn"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusEmailReceiveInvoice}
                  error={!!formErrors.cusEmailReceiveInvoice}
                  helperText={formErrors.cusEmailReceiveInvoice}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusEmailReceiveInvoice: e.target.value,
                    }));
                    setFormErrors((prev) => ({
                      ...prev,
                      cusEmailReceiveInvoice: "",
                    }));
                  }}
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Điện thoại liên hệ"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusPhoneNumber}
                  error={!!formErrors.cusPhoneNumber}
                  helperText={formErrors.cusPhoneNumber}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusPhoneNumber: e.target.value,
                    }));
                    setFormErrors((prev) => ({ ...prev, cusPhoneNumber: "" }));
                  }}
                  size="small"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Website"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={customer?.CusWebsite}
                  error={!!formErrors.cusWebsite}
                  helperText={formErrors.cusWebsite}
                  onChange={(e) => {
                    setCustomer((prev) => ({
                      ...prev,
                      CusWebsite: e.target.value,
                    }));

                    setFormErrors((prev) => ({ ...prev, cusWebsite: "" }));
                  }}
                  size="small"
                />
              </Grid>
              {/* <Grid size={6}>
                <TextField
                  select
                  label="Phương pháp tính thuế"
                  fullWidth
                  value={customer?.CusTaxCalculationMethod}
                  error={!!formErrors.cusTaxCalculationMethod}
                  helperText={formErrors.cusTaxCalculationMethod}
                  onChange={(e) =>
                    setCustomer((prev) => ({
                      ...prev,
                      CusTaxCalculationMethod: e.target.value,
                    }))
                  }
                  size="small"
                  required
                >
                  {filteredMethods.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid> */}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEditModal} color="primary">
              Hủy
            </Button>
            <Button
              onClick={handleSaveCustomer}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Lưu"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={openLinkModal} maxWidth="sm" fullWidth>
          <DialogTitle>Liên kết Merchant cha</DialogTitle>
          <Divider />
          <DialogContent>
            <TextField
              label="Mã Merchant cha"
              fullWidth
              size="small"
              value={customerNoParent}
              onChange={(e) => handleChangeCustomerNoParent(e.target.value)}
              required
              sx={{ mb: 1 }}
            ></TextField>
            {customerParent ? (
              <>
                {renderRow("Mã số thuế:", customerParent?.CusTaxId)}
                {renderRow("Tổ chức/Cá nhân:", customerParent?.CusCompanyName)}
                {renderRow("Địa chỉ:", customerParent?.CusAddress)}
                {renderRow(
                  "Người đại diện:",
                  customerParent?.CusRepresentativeName,
                )}
                {renderRow("Số điện thoại:", customerParent?.CusPhoneNumber)}
                {renderRow("Email liên hệ:", customerParent?.CusEmail)}
              </>
            ) : (
              <></>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleCloseLinkModal()} color="primary">
              Đóng
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleLinkParentCustomer(0, true)}
            >
              Liên kết
            </Button>
          </DialogActions>
        </Dialog>
        <ConfirmDialog
          open={confirmDialogState.open}
          title={confirmDialogState.title}
          content={confirmDialogState.content}
          onConfirm={confirmDialogState.onConfirm}
          onClose={confirmDialogState.onClose}
        />
      </Box>
    </LocalizationProvider>
  );
}
